import { f7, useStore } from 'framework7-react';
import { Router } from 'framework7/types';
import { FC, useCallback, useEffect, useState } from 'react';
import { AccountRegistPayment } from './AccountRegistPayment/AccountRegistPayment';
import { registerPaidyTokenApi } from '@/api/payment';
import { isApp } from '@/config/device';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { useCustomerInfo } from '@/hooks/api/customer/useCustomerInfo';
import { useGmo } from '@/hooks/useGmo';
import { useGmoRegisterAndEditRedirect } from '@/hooks/useGmoRegisterAndEditRedirect';
import { useLoadCustomer } from '@/hooks/useLoadCustomer';
import { usePaidy } from '@/hooks/usePaidy';
import { Car } from '@/types/api/customerApi';
import { storeDispatch } from '@/utils/store';
import { f7CustomBack } from '@/utils/utils';

interface AccountRegistPaymentPageProps {
  f7router: Router.Router;
  status?: string;
  currentCarId: string;
  fromPage?: string;
}
export const AccountRegistPaymentPage: FC<AccountRegistPaymentPageProps> = ({
  f7router,
  status,
  currentCarId,
  fromPage = paths.account,
}) => {
  const router = f7.views.main.router;
  const { m_car_id, t_order_id } =
    (useStore(store, 'carItemInfo') as Car) || {};
  const [actionUrl, setActionUrl] = useState<string>('');
  const [fromPagePath, setFromPagePath] = useState<string>(fromPage);
  const [currentStatus, setCurrentStatus] = useState(status);
  const isInfoLoaded = useStore(store, 'getIsInfoLoaded') as boolean;
  const customerId = window.localStorage.getItem('m_customer_id');
  const token =
    window.localStorage.getItem('token') === null
      ? undefined
      : window.localStorage.getItem('token');

  const { isLoggedIn } = useLoadCustomer(
    router,
    token === null ? undefined : token,
    Number(customerId),
    currentStatus,
    !status && store.state.authInfo.isLogin,
  );

  const { handleGmoRegisterAndEditRedirect } = useGmoRegisterAndEditRedirect();

  const { addCreditCardPaymentMethod, GMOCardEditParameters } = useGmo(
    'account_payment_regist',
  );
  const { launchPaidyCheckout } = usePaidy();
  const { setCustomerInfo } = useCustomerInfo(
    {
      m_customer_id: store.state.authInfo.m_customer_id,
    },
    store.state.authInfo.isLogin && !status,
  );

  const handleClickPaidyPayment = async () => {
    try {
      const result = await launchPaidyCheckout({ mode: 'token' });
      if (result.success && result.tokenId) {
        f7.preloader.show();
        const customerInfo = store.state.customerInfo;
        const res = await registerPaidyTokenApi({
          m_customer_id: store.state.authInfo.m_customer_id,
          paidy_token_id: result.tokenId,
          customer_name: `${customerInfo?.family_name ?? ''} ${
            customerInfo?.first_name ?? ''
          }`.trim(),
          customer_email: customerInfo?.email ?? '',
          phone_number: customerInfo?.phone_no1 ?? '',
        });
        f7.preloader.hide();
        if (res.data.success) {
          setCustomerInfo();
          f7.dialog.alert('', 'あと払い（ペイディ）の登録が完了しました');
        } else {
          f7.dialog.alert('', 'あと払い（ペイディ）の登録に失敗しました');
        }
      }
    } catch (error) {
      console.error('Paidy registration error:', error);
      f7.preloader.hide();
      f7.dialog.alert('', 'エラーが発生しました');
    }
  };

  const handleClickBack = () => {
    f7CustomBack();
  };

  const handleClickWithdrawFromBank = () => {
    const {
      globalTheme,
      isLoggedInToMaikuru,
      loginInfo: { loginType },
    } = store.state;
    window.localStorage.setItem('m_car_id', String(m_car_id));
    currentCarId && window.localStorage.setItem('currentCarId', currentCarId);
    window.localStorage.setItem('loginType', String(loginType));
    window.localStorage.setItem('t_order_id', String(t_order_id));
    window.localStorage.setItem('globalTheme', `${globalTheme}`);
    window.localStorage.setItem('fromPage', fromPage);
    window.localStorage.setItem(
      'isLoggedInToMaikuru',
      `${isLoggedInToMaikuru}`,
    );
    router.navigate(paths.registerBank, {
      props: {
        redirectPage: 'account_payment_regist',
      },
    });
  };

  const handleClickCreditCardPayment = async () => {
    const { token, m_customer_id } = store.state.authInfo;
    const {
      globalTheme,
      isLoggedInToMaikuru,
      loginInfo: { loginType },
    } = store.state;
    window.localStorage.setItem('token', token);
    window.localStorage.setItem('m_customer_id', m_customer_id.toString());
    window.localStorage.setItem('m_car_id', String(m_car_id));
    window.localStorage.setItem('loginType', String(loginType));
    window.localStorage.setItem('t_order_id', String(t_order_id));
    currentCarId && window.localStorage.setItem('currentCarId', currentCarId);
    window.localStorage.setItem('globalTheme', `${globalTheme}`);
    window.localStorage.setItem('fromPage', fromPage);
    window.localStorage.setItem(
      'isLoggedInToMaikuru',
      `${isLoggedInToMaikuru}`,
    );
    const returnUrl = paths.accountRegistPayment('gmo_success');
    const cancelUrl = paths.accountRegistPayment();
    try {
      const response = await addCreditCardPaymentMethod(returnUrl, cancelUrl); //GMOクレカ登録生成URL
      if (!response) {
        f7.dialog.alert('', `エラーが発生しました`);
        return;
      }
      if (
        response.error ||
        !response.success ||
        !('LinkUrl' in response.data)
      ) {
        f7.dialog.alert('', `エラーが発生しました`);
      } else {
        const GMOLinkTypePlusUrl = response.data.LinkUrl;
        if (isApp) {
          location.href = GMOLinkTypePlusUrl;
        } else {
          window.location.href = GMOLinkTypePlusUrl;
        }
      }
    } catch (error) {
      console.error('GMOパラメータの生成に失敗しました', error);
    } finally {
      // setIsProcessing(false);
    }
  };

  // 一回目のクリックでactionUrlが更新されたらもう一度クリック
  // useEffect(() => {
  //   document.getElementById('submit')?.click();
  // }, [actionUrl]);

  const afterGmoRedirect = useCallback(async () => {
    if (!isLoggedIn) return;
    // GMOからのリダイレクトに必要なパラメータ
    window.localStorage.removeItem('prepaid_registration_info');
    if (currentStatus) {
      storeDispatch('setMenu', 'menu', 'prepaid');
    }

    switch (currentStatus) {
      // GMOペイメント post →　maikuru バックエンド（api.mobicon.net）からのリダイレクト
      case 'gmo_success':
        if (isLoggedIn && isInfoLoaded) {
          setCurrentStatus(undefined);
          router.navigate(paths.accountRegistPayment('gmo_success'), {
            props: {
              popupOpened: true,
            },
          });
        }
        break;
      case 'gmo_cancel':
        if (isLoggedIn && isInfoLoaded) {
          setCurrentStatus(undefined);
          router.navigate(paths.accountRegistPayment('gmo_cancel'), {
            props: {
              popupOpened: true,
            },
          });
        }
        break;
      default:
        break;
    }
  }, [currentStatus, isLoggedIn, isInfoLoaded]);

  useEffect(() => {
    if (isLoggedIn && currentStatus) {
      handleGmoRegisterAndEditRedirect(currentStatus, isLoggedIn);
    }
  }, [isLoggedIn, currentStatus]);

  // GMOリダイレクト後のブラウザ履歴を修正する
  // 303リダイレクト後、ブラウザ履歴の前エントリがGMOページ(stg.link.mul-pay.jp)になっているため、
  // fromPagePathを挿入して戻る操作が正しく動作するようにする
  // replaceState/pushStateはpopstateを発火しないためF7に影響しない
  useEffect(() => {
    const storedFromPage = window.localStorage.getItem('fromPage');

    if (storedFromPage) {
      setFromPagePath(storedFromPage);
    }

    const f7History = f7.view.main.router.history;
    if (f7History.length <= 2) {
      const backPage = storedFromPage || fromPage;
      const currentUrl = window.location.pathname + window.location.search;
      window.history.replaceState(null, '', backPage);
      window.history.pushState(null, '', currentUrl);
    }

    return () => {
      window.localStorage.removeItem('fromPage');
    };
  }, []);

  return (
    <AccountRegistPayment
      status={status}
      GMOCardEditParameters={GMOCardEditParameters.data}
      handleClickBack={handleClickBack}
      handleClickCreditCardPayment={handleClickCreditCardPayment}
      handleClickWithdrawFromBank={handleClickWithdrawFromBank}
      handleClickPaidyPayment={handleClickPaidyPayment}
    />
  );
};
